import {
  IDEA_FREQUENCY_VALUES,
  IDEA_IMPACT_VALUES,
  IDEA_PROBLEM_MAX,
  IDEA_REACH_VALUES,
  IDEA_SOLUTION_MAX,
  IDEA_STATUS_VALUES,
  type IdeaFrequency,
  type IdeaImpact,
  type IdeaReach,
  TITLE_MAX,
} from '@cult/domain';
import { z } from 'zod';
import {
  BaseEntitySchema,
  CursorPageSchema,
  CursorQuerySchema,
  type SortOrder,
  SortOrderSchema,
  createdRangeFields,
  englishText,
  qField,
  timeZoneField,
  updatedAtField,
} from './common';
import { PublicAttachmentSchema, attachmentsInputField } from './uploads';

export const IdeaStatusSchema = z.enum(IDEA_STATUS_VALUES);

export const IdeaFrequencySchema = z
  .number()
  .int()
  .refine((v): v is IdeaFrequency => (IDEA_FREQUENCY_VALUES as number[]).includes(v), 'Pick how often this happens');

export const IdeaReachSchema = z
  .number()
  .int()
  .refine((v): v is IdeaReach => (IDEA_REACH_VALUES as number[]).includes(v), 'Pick who this affects');

export const IdeaImpactSchema = z
  .number()
  .int()
  .refine((v): v is IdeaImpact => (IDEA_IMPACT_VALUES as number[]).includes(v), 'Pick how much difference this makes');
export type IdeaStatus = z.infer<typeof IdeaStatusSchema>;

export const IDEA_SORTS = {
  NEW: 'new',
} as const;
export type IdeaSort = (typeof IDEA_SORTS)[keyof typeof IDEA_SORTS];
export const IDEA_SORT_VALUES = Object.values(IDEA_SORTS) as [IdeaSort, ...IdeaSort[]];
export const IdeaSortSchema = z.enum(IDEA_SORT_VALUES);

/** @alias SortOrder — asc/desc is not idea-specific */
export type IdeaOrder = SortOrder;
export const IdeaOrderSchema = SortOrderSchema;

export const IdeaListQuerySchema = CursorQuerySchema.extend({
  // Admin tables use offset pagination (page jumps + total); the feed keeps `cursor`.
  offset: z.coerce.number().int().min(0).optional(),
  status: IdeaStatusSchema.optional(),
  category: z.string().min(1).max(40).optional(),
  author: z.string().trim().min(1).max(100).optional(),
  q: qField,
  ...createdRangeFields,
  sort: IdeaSortSchema.optional(),
  order: IdeaOrderSchema.optional(),
  adminView: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional(),
});
export type IdeaListQuery = z.infer<typeof IdeaListQuerySchema>;

// Moderator-only, so always the admin view: no adminView flag, no pagination.
export const IdeaExportQuerySchema = IdeaListQuerySchema.omit({
  cursor: true,
  limit: true,
  offset: true,
  adminView: true,
}).extend({
  tz: timeZoneField,
});
export type IdeaExportQuery = z.infer<typeof IdeaExportQuerySchema>;

export const IdeaCategoryEmbedSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  color: z.string().nullable(),
});
export type IdeaCategoryEmbed = z.infer<typeof IdeaCategoryEmbedSchema>;

export const IdeaAuthorEmbedSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
});
export type IdeaAuthorEmbed = z.infer<typeof IdeaAuthorEmbedSchema>;

export const PublicIdeaSchema = BaseEntitySchema.extend({
  updatedAt: updatedAtField,
  number: z.number().int(),
  title: z.string(),
  // Ideas submitted before problem/solution were asked carry only body. For newer ones the API writes body as
  // problem and solution joined by a blank line, so previews, search and the CSV read the same for both.
  body: z.string(),
  problem: z.string().nullable(),
  solution: z.string().nullable(),
  status: IdeaStatusSchema,
  category: IdeaCategoryEmbedSchema,
  author: IdeaAuthorEmbedSchema.nullable(),
  isMyIdea: z.boolean(),
  closedAt: z.string().nullable(),
  // Null on ideas submitted before frequency, reach and impact were asked.
  frequency: IdeaFrequencySchema.nullable(),
  reach: IdeaReachSchema.nullable(),
  impact: IdeaImpactSchema.nullable(),
  pointsAwarded: z.boolean(),
  attachments: z.array(PublicAttachmentSchema),
});
export type PublicIdea = z.infer<typeof PublicIdeaSchema>;

const ideaTitleField = englishText(z.string().trim().min(3).max(TITLE_MAX));
const ideaBodyField = englishText(z.string().trim().min(10).max(5000));
const ideaProblemField = englishText(z.string().trim().min(10).max(IDEA_PROBLEM_MAX));
const ideaSolutionField = englishText(z.string().trim().min(5).max(IDEA_SOLUTION_MAX));

export const IdeaCreateInputSchema = z.object({
  title: ideaTitleField,
  problem: ideaProblemField,
  solution: ideaSolutionField,
  categoryId: z.string().min(1),
  countryId: z.string().min(1),
  departmentId: z.string().min(1),
  frequency: IdeaFrequencySchema,
  reach: IdeaReachSchema,
  impact: IdeaImpactSchema,
  attachments: attachmentsInputField,
});
export type IdeaCreateInput = z.infer<typeof IdeaCreateInputSchema>;

export const IdeaUpdateInputSchema = z
  .object({
    title: ideaTitleField.optional(),
    // body edits ideas from before problem/solution; newer ideas edit problem and solution instead.
    body: ideaBodyField.optional(),
    problem: ideaProblemField.optional(),
    solution: ideaSolutionField.optional(),
    categoryId: z.string().min(1).optional(),
    frequency: IdeaFrequencySchema.optional(),
    reach: IdeaReachSchema.optional(),
    impact: IdeaImpactSchema.optional(),
    attachments: attachmentsInputField,
  })
  .refine((d) => Object.keys(d).length > 0, { message: 'At least one field is required' })
  .refine((d) => d.body === undefined || (d.problem === undefined && d.solution === undefined), {
    message: 'Send either body or problem and solution, not both',
    path: ['body'],
  });
export type IdeaUpdateInput = z.infer<typeof IdeaUpdateInputSchema>;

export const IdeaListResponseSchema = CursorPageSchema.extend({
  ideas: z.array(PublicIdeaSchema),
  // Present only in offset (admin) mode; the cursor feed omits it.
  total: z.number().int().nonnegative().optional(),
});
export type IdeaListResponse = z.infer<typeof IdeaListResponseSchema>;

export const IdeaCountResponseSchema = z.object({
  count: z.number().int().nonnegative(),
});
export type IdeaCountResponse = z.infer<typeof IdeaCountResponseSchema>;

// Closing takes no reason: the author gets a standard "idea closed" email instead.
// The optional note only lands in the status log.
export const IdeaTransitionInputSchema = z.object({
  to: IdeaStatusSchema,
  note: z.string().max(2000).optional(),
});
export type IdeaTransitionInput = z.infer<typeof IdeaTransitionInputSchema>;

export const IdeaAwardPointsResponseSchema = z.object({
  awarded: z.boolean(),
  alreadyAwarded: z.boolean(),
});
export type IdeaAwardPointsResponse = z.infer<typeof IdeaAwardPointsResponseSchema>;

export const IdeaStatusLogEntrySchema = z.object({
  id: z.string(),
  fromStatus: IdeaStatusSchema.nullable(),
  toStatus: IdeaStatusSchema,
  changedBy: IdeaAuthorEmbedSchema,
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type IdeaStatusLogEntry = z.infer<typeof IdeaStatusLogEntrySchema>;

export const IdeaStatusLogResponseSchema = z.object({
  entries: z.array(IdeaStatusLogEntrySchema),
});
export type IdeaStatusLogResponse = z.infer<typeof IdeaStatusLogResponseSchema>;
